import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Alert,
  List,
  ListItem,
} from '@mui/material';

interface InsufficientInfoDialogProps {
  open: boolean;
  questionText: string;
  insufficientInfo?: {
    availableContext: string;
    missingInformation: string[];
    suggestedSources: string[];
  };
  onClose: () => void;
  onSubmit: (context: string) => void;
}

export default function InsufficientInfoDialog({
  open,
  questionText,
  insufficientInfo,
  onClose,
  onSubmit,
}: InsufficientInfoDialogProps) {
  const [manualContext, setManualContext] = useState('');

  const handleSubmit = () => {
    if (manualContext.trim()) {
      onSubmit(manualContext);
      setManualContext('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Provide Additional Context</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Question:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {questionText}
            </Typography>
          </Box>

          {insufficientInfo && (
            <>
              <Alert severity="info">
                <Typography variant="body2" gutterBottom>
                  <strong>What we have:</strong>
                </Typography>
                <Typography variant="body2">{insufficientInfo.availableContext}</Typography>
              </Alert>

              {insufficientInfo.missingInformation.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Missing Information:
                  </Typography>
                  <List dense>
                    {insufficientInfo.missingInformation.map((info, idx) => (
                      <ListItem key={idx} disablePadding>
                        <Typography variant="body2">• {info}</Typography>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {insufficientInfo.suggestedSources.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Suggested Sources:
                  </Typography>
                  <List dense>
                    {insufficientInfo.suggestedSources.map((source, idx) => (
                      <ListItem key={idx} disablePadding>
                        <Typography variant="body2">• {source}</Typography>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Provide Additional Context:
            </Typography>
            <TextField
              multiline
              fullWidth
              rows={8}
              value={manualContext}
              onChange={(e) => setManualContext(e.target.value)}
              placeholder="Enter any additional information, examples, or context that would help answer this question..."
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              You can paste relevant text from documents, provide specific examples, or describe capabilities that
              should be mentioned.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!manualContext.trim()}>
          Submit & Regenerate
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Box(props: { children: React.ReactNode }) {
  return <div>{props.children}</div>;
}
